import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Toaster } from "sonner";
import { Check, Phone, ArrowDown, Building2, Store, Briefcase, KeyRound, GraduationCap, Landmark, ShieldCheck, CalendarClock, FileText, UserCheck } from "lucide-react";
import { SERVICES, COMPANY } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import CommercialSurveyForm from "@/components/CommercialSurveyForm";
import { trackCallConversion } from "@/lib/analytics";
import { useSeo } from "@/hooks/useSeo";

const SECTORS = [
  { icon: Briefcase, title: "Facilities & property managers", copy: "Reliable, scheduled exterior maintenance across single sites or full portfolios." },
  { icon: KeyRound, title: "Letting agents & landlords", copy: "Keep managed lettings presentable between tenancies and protect the building fabric." },
  { icon: Store, title: "Retail & hospitality", copy: "Bright, welcoming storefronts, signage and frontages that reflect your brand." },
  { icon: Building2, title: "Offices & commercial units", copy: "Cladding, render, glazing surrounds and roofs cleaned safely with minimal disruption." },
  { icon: GraduationCap, title: "Schools & public buildings", copy: "Fully insured, DBS-conscious teams working to agreed safe systems of work." },
  { icon: Landmark, title: "Managing agents & blocks", copy: "Communal roofs, gutters and rendered elevations maintained across residential blocks." },
];

const WHY = [
  { icon: ShieldCheck, title: "Fully insured & RAMS", copy: "Comprehensive public liability cover, method statements and risk assessments provided for every site." },
  { icon: CalendarClock, title: "Flexible, low-disruption scheduling", copy: "Out-of-hours, weekend and phased work to keep your premises trading and your tenants undisturbed." },
  { icon: FileText, title: "Clear quotes & invoicing", copy: "Fixed-price proposals, purchase-order friendly invoicing and tidy documentation for your records." },
  { icon: UserCheck, title: "One point of contact", copy: "A single, professional contact from survey to sign-off — plus planned maintenance contracts on request." },
];

export default function Commercial() {
  const lenis = useLenis();
  const scrollToSurvey = () => {
    if (lenis) lenis.scrollTo("#site-survey", { offset: -80 });
    else document.getElementById("site-survey")?.scrollIntoView({ behavior: "smooth" });
  };
  useSeo(
    "Commercial Exterior Cleaning Lancashire | Managed Properties & Business | StoneCrest",
    "Commercial exterior cleaning across Lancashire for facilities managers, letting agents, landlords, retail, offices and managed properties. Fully insured, RAMS-backed, low-disruption roof, render, gutter and cladding cleaning with planned maintenance contracts. Free fixed-price quotes from StoneCrest Exterior Specialists.",
    "https://stonecrestexteriors.co.uk/commercial"
  );

  return (
    <div data-testid="commercial-page">
      <Toaster position="top-center" />
      <PageHeader
        overline="For Business · Commercial & Managed Properties"
        lines={["Exterior cleaning", "for business."]}
        subtitle="StoneCrest works with facilities managers, letting agents, landlords and business owners across Lancashire to keep commercial and managed properties clean, safe and presentable — delivered with full insurance, agreed safe systems of work and minimal disruption to your operation."
      />

      <section className="px-6 md:px-12 max-w-7xl mx-auto -mt-4 md:-mt-6 pb-2">
        <button type="button" onClick={scrollToSurvey} data-testid="hero-survey-btn" className="group inline-flex items-center gap-2 bg-[var(--oxblood)] text-white px-8 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors">
          Request a Site Survey <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
        </button>
      </section>

      {/* Feature image band */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-6">
        <Reveal>
          <div className="relative aspect-[21/9] overflow-hidden border border-[var(--border)]">
            <img
              src="https://images.unsplash.com/photo-1652974135481-e82f7630eaa2?w=1600&q=85"
              alt="Specialist commercial exterior cleaning at height on a large building"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute bottom-6 left-6 md:bottom-8 md:left-8 text-white font-serif text-2xl md:text-3xl max-w-md leading-tight">
              Trusted with commercial exteriors, at height and at scale.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Intro */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto py-12 md:py-16">
        <div className="space-y-5 text-lg text-[var(--muted)] leading-relaxed">
          <Reveal><p>First impressions matter for any business. Moss-covered roofs, streaked render, blocked gutters and grimy frontages don't just look neglected — they can damage the building fabric and put people off before they've walked through the door. StoneCrest restores and maintains commercial exteriors with professional, low-pressure methods that are safe for both your property and the people around it.</p></Reveal>
          <Reveal delay={0.05}><p>Whether you manage a single unit or a full portfolio, we work to your schedule and your compliance requirements — providing risk assessments and method statements up front, keeping disruption to a minimum, and giving you a single, reliable point of contact from survey through to sign-off.</p></Reveal>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-24">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Who We Work With</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-12 max-w-2xl">Built for businesses and the people who manage property.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            {SECTORS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04} className="bg-white p-8 md:p-9">
                <s.icon className="w-7 h-7 text-[var(--oxblood)]" strokeWidth={1.4} />
                <h3 className="font-serif text-2xl mt-5 mb-2">{s.title}</h3>
                <p className="text-[var(--muted)] leading-relaxed">{s.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-24">
        <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Commercial Services</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-10 max-w-2xl">The full range, scaled for commercial premises.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`commercial-service-${s.slug}`} className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors">
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">{s.name}</span>
            </Link>
          ))}
        </div>
        <p className="mt-6 text-sm text-[var(--muted)]">Need something specific — cladding, signage, car parks or communal areas? <Link to="/contact" className="text-[var(--oxblood)] underline">Tell us about your site</Link> and we'll tailor a plan.</p>
      </section>

      {/* Why choose us */}
      <section className="bg-[var(--ink)] text-white">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-24">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood-light)] mb-4">Why StoneCrest</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-12 max-w-2xl">A supplier your compliance team will be happy with.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="flex gap-5">
                  <w.icon className="w-8 h-8 text-[var(--oxblood-light)] shrink-0" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-serif text-2xl mb-2">{w.title}</h3>
                    <p className="text-white/70 leading-relaxed">{w.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA row */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto py-16 md:py-20 text-center">
        <h2 className="font-serif font-light text-3xl md:text-4xl mb-6">Request a commercial quote or site survey.</h2>
        <p className="text-[var(--muted)] max-w-xl mx-auto mb-8">Tell us about your property or portfolio and we'll arrange a free assessment and fixed-price proposal — with RAMS included.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/quote" data-testid="commercial-quote-btn" className="bg-[var(--oxblood)] text-white px-8 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors">Request a Commercial Quote</Link>
          <a href={COMPANY.phoneHref} onClick={trackCallConversion} data-testid="commercial-call-btn" className="border border-[var(--ink)] px-8 py-4 rounded-full tracking-wide hover:bg-[var(--ink)] hover:text-white transition-all flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
        </div>
      </section>

      {/* Site survey form */}
      <section id="site-survey" className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-3xl mx-auto py-16 md:py-24">
          <div className="bg-white p-8 md:p-12 border border-[var(--border)]">
            <CommercialSurveyForm />
          </div>
        </div>
      </section>

      <CTASection title="Reliable exterior care for your business." sub="Planned maintenance or one-off cleans — insured, professional and low-disruption." />
    </div>
  );
}
